import { inject, Injectable } from '@angular/core';
import { AvatarOrderDataResponse, AvatarResponse } from '@api/client';
import { CallOrder } from '../interfaces/call-order.interface';
import { LocalStorageService } from '@services/local-storage.service';
import { CALL_ORDER_TYPE_NUMBERS } from '../call.constants';

@Injectable({
  providedIn: 'root'
})
export class CallSessionService {
  private readonly localStorage: LocalStorageService = inject(LocalStorageService);
  private readonly selectedCallOrderStorageKey: string = 'selectedCallOrder';
  private selectedOrderState: CallOrder | null = null;

  constructor() {
    this.selectedOrderState = this.readStoredOrder();
  }

  public get selectedOrder(): CallOrder | null {
    return this.selectedOrderState;
  }

  public setSelectedOrder(response: AvatarOrderDataResponse, isCustomer: boolean): void {
    const mappedOrder: CallOrder | null = this.mapOrderResponse(response, isCustomer);
    if (!mappedOrder) {
      console.error('No data in call order response');
      return;
    }
    if (mappedOrder.type !== CALL_ORDER_TYPE_NUMBERS.AUDIO_CALL) {
      console.error('Call session rejected non-audio order type', mappedOrder.type);
      this.clearSelectedOrder();
      return;
    }

    this.setSelectedOrderData(mappedOrder);
  }

  public setSelectedOrderForUser(response: AvatarOrderDataResponse, userId: number | null | undefined): boolean {
    const isCustomer: boolean | null = this.resolveOrderIsCustomer(response, userId);
    if (isCustomer === null) {
      console.error('Current user is not a participant of call order');
      this.clearSelectedOrder();
      return false;
    }

    this.setSelectedOrder(response, isCustomer);
    return this.selectedOrderState !== null;
  }

  public clearSelectedOrder(): void {
    this.selectedOrderState = null;
    this.localStorage.removeItem(this.selectedCallOrderStorageKey);
  }

  private setSelectedOrderData(order: CallOrder): void {
    this.selectedOrderState = order;
    this.localStorage.setItem(this.selectedCallOrderStorageKey, order);
  }

  private resolveOrderIsCustomer(response: AvatarOrderDataResponse, userId: number | null | undefined): boolean | null {
    const normalizedUserId: number | null = this.normalizeUserId(userId);
    if (normalizedUserId === null) return null;

    const fromId: number | null = this.normalizeUserId(response?.data?.from?.id ?? response?.data?.from_id);
    if (fromId === normalizedUserId) return true;

    const toId: number | null = this.normalizeUserId(response?.data?.to?.id ?? response?.data?.to_id);
    if (toId === normalizedUserId) return false;

    return null;
  }

  private normalizeUserId(userId: number | null | undefined): number | null {
    if (typeof userId !== 'number') return null;
    if (!Number.isFinite(userId) || userId <= 0) return null;
    return userId;
  }

  private mapOrderResponse(response: AvatarOrderDataResponse, isCustomer: boolean): CallOrder | null {
    if (!response?.data) return null;

    const customer: AvatarResponse | null | undefined = response.data?.from;
    const executor: AvatarResponse | null | undefined = response.data?.to;

    return {
      id: response.data?.id,
      type: response.data?.type,
      isCustomer,
      myUserId: isCustomer ? customer?.id : executor?.id,
      partnerId: isCustomer ? executor?.id : customer?.id,
      partnerAvatar: isCustomer ? executor?.avatar : customer?.avatar,
      partnerName: isCustomer ? executor?.name : customer?.name,
      partnerSurname: isCustomer ? executor?.surname : customer?.surname,
      partnerOfficial: isCustomer ? executor?.official : customer?.official,
    };
  }

  private readStoredOrder(): CallOrder | null {
    const storedOrder: CallOrder | null = this.localStorage.getItem(this.selectedCallOrderStorageKey, true);
    const normalizedOrder: CallOrder | null = this.normalizeStoredOrder(storedOrder);
    if (!normalizedOrder && storedOrder) {
      this.localStorage.removeItem(this.selectedCallOrderStorageKey);
    }
    return normalizedOrder;
  }

  private normalizeStoredOrder(order: CallOrder | null): CallOrder | null {
    if (!order) return null;
    if (order.type !== CALL_ORDER_TYPE_NUMBERS.AUDIO_CALL) return null;
    if (typeof order.id !== 'number' || !Number.isFinite(order.id) || order.id <= 0) return null;
    return order;
  }
}
